/**
 * Lab Backend-UI `lineSessionOddsHelpers` (filter.js / #5783) for Game Detail:
 * Odds Placed, Avg. Odds (full vs partial), Matched Stake.
 */

export type LineSessionOddsBet = {
  type?: string | null
  stake?: number | null
  /** Lab matchedAmt — prefer over amountExecuted for partial detection. */
  matchedAmt?: number | null
  amountExecuted?: number | null
  amountToBePlaced?: number | null
  runs?: number | null
  odds?: number | null
  betRate?: number | null
  averagePriceMatched?: number | null
  betfairLimitOrderPrice?: number | null
  executedOdds?: number | null
}

function toNum(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function isBackSide(type: string | null | undefined): boolean {
  const t = String(type ?? '')
    .trim()
    .toUpperCase()
  return t === 'BACK' || t === 'YES'
}

/** BACK/YES → max; LAY/NO → min. */
export function pickBySide(
  type: string | null | undefined,
  candidates: number[],
  fallback: number | null,
): number | null {
  const vals = candidates.filter((n) => Number.isFinite(n))
  if (!vals.length) return fallback
  return isBackSide(type) ? Math.max(...vals) : Math.min(...vals)
}

function placedOdds(bet: LineSessionOddsBet): number | null {
  const candidates: number[] = []
  const limit = toNum(bet.betfairLimitOrderPrice)
  const runs = toNum(bet.runs)
  const odds = toNum(bet.odds)
  const betRate = toNum(bet.betRate)
  if (limit != null && limit > 0 && limit !== 2) candidates.push(limit)
  if (runs != null && runs > 0 && runs !== 2) candidates.push(runs)
  if (odds != null && odds > 0 && odds !== 2) candidates.push(odds)
  if (betRate != null && betRate > 0 && betRate !== 2) candidates.push(betRate)
  if (!candidates.length) return runs ?? limit ?? odds
  return pickBySide(bet.type, candidates, candidates[0]!)
}

/** Partial only when matched stake clearly below stake (Lab hasUnmatchedStake). */
export function hasUnmatchedStake(bet: LineSessionOddsBet): boolean {
  const stake = toNum(bet.stake)
  const matched = toNum(bet.matchedAmt ?? bet.amountExecuted)
  if (stake == null || matched == null || stake <= 0) return false
  return matched < stake * 0.999
}

/**
 * Lab Odds Placed display value (ceil in UI elsewhere).
 * Prefers limit / runs candidates with BACK high / LAY low.
 */
export function lineSessionPlacedOdds(bet: LineSessionOddsBet): number | null {
  const v = placedOdds(bet)
  return v != null && Number.isFinite(v) ? v : null
}

/**
 * Lab Avg. Odds:
 * - Full match → raw averagePriceMatched (no ceil)
 * - Partial → BACK highest / LAY lowest of fill+placed, then ceil
 */
export function lineSessionAvgOdds(bet: LineSessionOddsBet): number | null {
  const avg = toNum(bet.averagePriceMatched)
  const executed = toNum(bet.executedOdds)
  const matched = avg != null && avg !== 2 ? avg : executed
  const placed = placedOdds(bet)
  const partial =
    hasUnmatchedStake(bet) &&
    placed != null &&
    matched != null &&
    Math.abs(placed - matched) > 0.05

  if (partial) {
    const candidates: number[] = []
    if (avg != null && avg !== 2) candidates.push(avg)
    if (executed != null && executed !== 2) candidates.push(executed)
    if (placed != null) candidates.push(placed)
    const picked = pickBySide(bet.type, candidates, matched)
    return picked == null ? null : Math.ceil(picked)
  }

  if (matched != null && matched > 0 && matched !== 2) return matched
  return placed
}

/**
 * Lab Matched Stake: matchedAmt, else stake × executed / toPlace, else amountExecuted.
 */
export function lineSessionMatchedStake(bet: LineSessionOddsBet): number {
  const matchedAmt = toNum(bet.matchedAmt)
  if (matchedAmt != null && matchedAmt > 0) return matchedAmt
  const stake = toNum(bet.stake) ?? 0
  const executed = toNum(bet.amountExecuted)
  const toPlace = toNum(bet.amountToBePlaced)
  if (executed != null && toPlace != null && toPlace > 0 && stake > 0) {
    return (stake * executed) / toPlace
  }
  if (executed != null && executed > 0) return executed
  return stake > 0 ? stake : 0
}
